import {
  Button,
  Column,
  Copy,
  Flex,
  Heading,
  Row,
  Square,
  Squish,
  Stack,
  SvgMagnifyingGlass,
  SvgX,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { Input } from 'src/components/Input/Input';
import { Modal } from 'src/components/Modal/Modal';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import {
  ILocDiner,
  SearchStatus,
  useLocDinersSearch,
} from 'src/utilities/useSearchHooks';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { RouterLink } from 'src/components/RouterLink/RouterLink';

const FIXED_BODY_HEIGHT = '300px';
const DEBOUNCE_SEARCH_TIMING = 200;

export interface IAddPeopleToLocModalProps {
  headerAndButtonText?: string;
  onClose: () => void;
  onAdd: (dinersSelected: string[]) => void;
  locId: string;
}

export default function AddPeopleToLocModal({
  headerAndButtonText = 'Add people',
  onClose,
  onAdd,
  locId,
}: IAddPeopleToLocModalProps): JSX.Element {
  const { isPhone, isDesktop } = useScreenSizes();
  const { corpid } = useParams<{ corpid: string }>();

  const [isSelectionActive, setIsSelectionActive] = useState(false);
  const usingCustomSearch = useRef(false);

  const {
    resultsList: diners,
    totalResults,
    searchStatus: dinersSearchStatus,
    hasMoreResults,
    loadMoreNumber,
    doSearch: getDiners,
    error,
  } = useLocDinersSearch(corpid as string, locId as string);

  const { register, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: { dinersSelected: {}, selectAll: false },
  });

  const debouncedSearchTerm = useDebouncedValue(
    watch('searchTerm', '') as string,
    DEBOUNCE_SEARCH_TIMING
  );
  const dinersSelected = watch('dinersSelected');
  const selectAll = watch('selectAllDiners', false);

  useEffect(() => {
    const activeSelections = Object.keys(dinersSelected)
      .map((k) => dinersSelected[k])
      .some((v) => !!v);

    setIsSelectionActive(activeSelections);
  }, [dinersSelected]);

  useEffect(() => {
    setAllCheckboxSelection(selectAll as boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  useEffect(() => {
    if (dinersSearchStatus === SearchStatus.ERROR) {
      pushErrorFromAPI(error, {
        fallbackMessage:
          'Sorry, there was an issue retrieving the diners for this account',
      });
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dinersSearchStatus]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      resetSelections();
      usingCustomSearch.current = true;
      getDiners(
        { query: debouncedSearchTerm, include_diners_not_in_tag: true },
        false
      );
    } else {
      getDiners({ include_diners_not_in_tag: true }, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  function getPeopleSelected(): string[] {
    return Object.keys(dinersSelected).reduce((idList, key) => {
      if (dinersSelected[key]) idList.push(key);
      return idList;
    }, []);
  }

  function loadMore() {
    getDiners({ include_diners_not_in_tag: true }, true);
    resetSelections();
  }

  function resetSelections() {
    setValue('selectAllDiners', false);
    setAllCheckboxSelection(false);
  }

  function setAllCheckboxSelection(selected: boolean) {
    Object.keys(dinersSelected).forEach((dinerId) => {
      setValue(`dinersSelected[${dinerId}]`, selected);
    });
  }

  function getSortedListOfDiners() {
    // Need to send all diners members of the LOC to the buttom of the list
    return diners.sort((a: ILocDiner, b: ILocDiner) => {
      const aMember = +a.member;
      const bMember = +b.member;
      if (aMember === bMember) {
        return 0;
      }
      if (aMember > bMember) {
        return 1;
      }
      return -1;
    });
  }

  return (
    <Modal size="sm">
      <Square position="relative">
        <Table
          hideHeader={false}
          isLoading={dinersSearchStatus === SearchStatus.LOADING}
          bodyMinHeight={FIXED_BODY_HEIGHT}
          bodyMaxHeight={isDesktop ? FIXED_BODY_HEIGHT : 'auto'}
          headerInsets={isDesktop ? 'lg' : 'md'}
          header={() => (
            <>
              <Stack y="sm">
                <Button
                  variant="tertiary"
                  type="button"
                  onClick={onClose}
                  className="removeButtonPadding"
                >
                  <SvgX size="24px" />
                </Button>
              </Stack>

              <Stack y="sm">
                <Heading variant="h1">{headerAndButtonText}</Heading>
              </Stack>

              <Stack y="sm">
                <Input
                  key="searchTerm"
                  ariaLabel="Search by name or email"
                  placeholder="Search by name or email"
                  name="searchTerm"
                  id="searchTerm"
                  prefixIcon={
                    <Copy variant="body" color="secondaryText">
                      <SvgMagnifyingGlass size="16px" />
                    </Copy>
                  }
                  ref={register}
                />
              </Stack>

              <Stack y="sm">
                <Flex justifyContent="space-between">
                  <Copy variant="body" color="secondaryText">{`${
                    getPeopleSelected().length
                  } selected`}</Copy>

                  <Copy
                    variant="body"
                    color="secondaryText"
                  >{`${diners.length} of ${totalResults}`}</Copy>
                </Flex>
              </Stack>

              <Row gutter={0}>
                <Column gutter={0} desktop={1} tablet={1} phone={1}>
                  <Checkbox
                    id="selectAllDiners"
                    name="selectAllDiners"
                    isMultipleSelection
                    ariaLabel="select all diners"
                    ref={register}
                    cancelLabelBottomMargin
                  />
                </Column>

                {isDesktop && (
                  <>
                    <Column gutter={0} desktop={5}>
                      <Copy variant="body" color="secondaryText">
                        Name
                      </Copy>
                    </Column>
                    <Column gutter={0} desktop={6}>
                      <Copy variant="body" color="secondaryText">
                        Email
                      </Copy>
                    </Column>
                  </>
                )}

                {!isDesktop && (
                  <>
                    <Column gutter={24} tablet={6} phone={6}>
                      <Copy variant="body" color="secondaryText">
                        Name, Email
                      </Copy>
                    </Column>
                  </>
                )}
              </Row>
            </>
          )}
          body={() => (
            <>
              {diners.length > 0 && (
                <ul className="removeListStyles">
                  {getSortedListOfDiners().map((diner) => (
                    <div key={diner.id}>
                      {isDesktop ? (
                        <Squish
                          as="li"
                          inset={isDesktop ? 'lg' : 'md'}
                          borderBottom="1px solid"
                          borderColor="lightLine"
                        >
                          <Row gutter={0}>
                            <Column gutter={0} desktop={1}>
                              <Flex alignItems="center" height="100%">
                                {!diner.member && (
                                  <Checkbox
                                    cancelLabelBottomMargin
                                    id={`dinersSelected[${diner.id}]`}
                                    name={`dinersSelected[${diner.id}]`}
                                    ariaLabel={`select diner ${diner.name}`}
                                    ref={register}
                                  />
                                )}

                                {/* Shows an empty checkbox for diner members of the LOC */}
                                {diner.member && (
                                  <Checkbox
                                    disabled
                                    id="dummycheckbox"
                                    name="dummycheckbox"
                                  />
                                )}
                              </Flex>
                            </Column>
                            <Column gutter={0} desktop={5}>
                              <Flex alignItems="center" height="100%">
                                <div className="ellipsisOverflow">
                                  <Copy
                                    variant="body"
                                    color={
                                      diner.member
                                        ? 'globals.stone20a'
                                        : 'primaryText'
                                    }
                                  >
                                    {diner.name}
                                  </Copy>
                                </div>
                              </Flex>
                            </Column>
                            <Column gutter={0} desktop={6}>
                              <Flex alignItems="center" height="100%">
                                <div className="ellipsisOverflow">
                                  <Copy
                                    variant="body"
                                    color={
                                      diner.member
                                        ? 'globals.stone20a'
                                        : 'primaryText'
                                    }
                                  >
                                    {diner.email}
                                  </Copy>
                                </div>
                              </Flex>
                            </Column>
                          </Row>
                        </Squish>
                      ) : (
                        <Squish
                          inset="md"
                          borderBottom="1px solid"
                          borderColor="lightLine"
                        >
                          <Flex
                            alignItems="flex-start"
                            flexDirection="row"
                            justifyContent="space-between"
                          >
                            <Stack>
                              <Flex alignItems="flex-start">
                                <Stack x="md">
                                  <Flex alignItems="center" height="100%">
                                    {!diner.member && (
                                      <Checkbox
                                        cancelLabelBottomMargin
                                        id={`dinersSelected[${diner.id}]`}
                                        name={`dinersSelected[${diner.id}]`}
                                        ariaLabel={`select diner ${diner.name}`}
                                        ref={register}
                                      />
                                    )}

                                    {/* Shows an empty checkbox for diner members of the LOC */}
                                    {diner.member && (
                                      <Checkbox
                                        disabled
                                        id="dummycheckbox"
                                        name="dummycheckbox"
                                      />
                                    )}
                                  </Flex>
                                </Stack>

                                <Flex flexDirection="column">
                                  <Square>
                                    <Stack y="xxx-sm">
                                      <Copy
                                        variant="body"
                                        color={
                                          diner.member
                                            ? 'globals.stone20a'
                                            : 'primaryText'
                                        }
                                      >
                                        {diner.name}
                                      </Copy>
                                    </Stack>
                                  </Square>
                                  <Square>
                                    <Copy
                                      variant="body"
                                      color={
                                        diner.member
                                          ? 'globals.stone20a'
                                          : 'primaryText'
                                      }
                                    >
                                      {diner.email}
                                    </Copy>
                                  </Square>
                                </Flex>
                              </Flex>
                            </Stack>
                          </Flex>
                        </Squish>
                      )}
                    </div>
                  ))}

                  {hasMoreResults && (
                    <Square
                      as="li"
                      inset="sm"
                      borderBottom="1px solid"
                      borderColor="lightLine"
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        minHeight="72px"
                      >
                        <Square inset="sm" width="100%">
                          <Button
                            data-testid="load-more-button"
                            variant="secondary"
                            type="button"
                            buttonSize="large"
                            block
                            onClick={loadMore}
                          >
                            <span>Load {loadMoreNumber} more</span>
                          </Button>
                        </Square>
                      </Flex>
                    </Square>
                  )}
                </ul>
              )}

              <Flex alignItems="center" justifyContent="center">
                <Square inset="lg" style={{ textAlign: 'center' }}>
                  <Copy variant="body" color="secondaryText">
                    Don't see your people here? Add them to your company
                    <br /> on the{' '}
                    <RouterLink href={`/corporate/management/${corpid}/people`}>
                      People page
                    </RouterLink>
                  </Copy>
                </Square>
              </Flex>

              {diners.length === 0 &&
                dinersSearchStatus === SearchStatus.COMPLETE && (
                  <NoResultsMsg
                    color="secondaryText"
                    message="We couldn’t find who you were looking for. Give it another go."
                  />
                )}
            </>
          )}
          isSignupOrSetupTable={false}
          justifyFooter="space-between"
          footerMinHeight="auto"
          footerBg="untintedBackground"
          showFooter
          footer={() => (
            <Squish inset="lg" width="100%">
              <Flex
                alignItems="center"
                justifyContent="space-between"
                flexDirection={isPhone ? 'column' : 'row'}
              >
                <Stack
                  y={isPhone ? 'md' : 'none'}
                  x={!isPhone ? 'md' : 'none'}
                  width="100%"
                >
                  <Button
                    block
                    variant="primary"
                    buttonSize="medium"
                    disabled={!isSelectionActive}
                    onClick={() => onAdd(getPeopleSelected())}
                  >
                    {`${headerAndButtonText} (${getPeopleSelected().length})`}
                  </Button>
                </Stack>

                <Button
                  block
                  variant="secondary"
                  buttonSize="medium"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </Flex>
            </Squish>
          )}
        />
      </Square>
    </Modal>
  );
}
